import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMcpConnectors } from "../../../hooks/useMcpConnectors";
import { buildSurfaceCapabilities } from "../../../lib/surface-capabilities";
import type { McpServerId } from "../../../types/mcp";
import {
  getCanonComposerConnectors,
  readComposerConnectorIds,
  writeComposerConnectorIds,
} from "./composerConnectors";

export function useComposerConnectors(connectorScopeId?: string | null) {
  const { servers, activeIds, toggle: toggleConnector } = useMcpConnectors();
  const allowConsulControl = useAllowConsulControl();
  const connectorItems = useMemo(
    () =>
      getCanonComposerConnectors(servers, activeIds, { allowConsulControl }),
    [activeIds, allowConsulControl, servers],
  );
  const [attachedConnectorIds, setAttachedConnectorIds] = useState<
    McpServerId[]
  >(() =>
    filterConnectorIds(readComposerConnectorIds(connectorScopeId), {
      allowConsulControl,
    }),
  );
  const scopeRef = useRef(connectorScopeId);

  useEffect(() => {
    const previousScopeId = scopeRef.current;
    const previousIds = readComposerConnectorIds(previousScopeId);
    const currentIds = readComposerConnectorIds(connectorScopeId);
    const shouldPromoteSurfaceAttachments =
      previousScopeId !== connectorScopeId &&
      connectorScopeId?.startsWith("conversation:") &&
      currentIds.length === 0 &&
      previousIds.length > 0;
    const nextIds = filterConnectorIds(
      shouldPromoteSurfaceAttachments ? previousIds : currentIds,
      { allowConsulControl },
    );
    if (shouldPromoteSurfaceAttachments) {
      writeComposerConnectorIds(nextIds, connectorScopeId);
    }
    if (nextIds.length !== currentIds.length) {
      writeComposerConnectorIds(nextIds, connectorScopeId);
    }
    scopeRef.current = connectorScopeId;
    setAttachedConnectorIds(nextIds);
  }, [allowConsulControl, connectorScopeId]);

  const handleAttachConnector = useCallback(
    (id: string) => {
      const connectorId = id as McpServerId;
      if (connectorId === "consul-control" && !allowConsulControl) return;
      const isAttached = attachedConnectorIds.includes(connectorId);
      setAttachedConnectorIds((current) => {
        const next = current.includes(connectorId)
          ? current.filter((item) => item !== connectorId)
          : [...current, connectorId];
        writeComposerConnectorIds(next, connectorScopeId);
        return next;
      });

      const server = servers.find((item) => item.id === connectorId);
      if (server && !server.enabled && !isAttached) {
        void toggleConnector(connectorId, true);
      }
    },
    [
      allowConsulControl,
      attachedConnectorIds,
      connectorScopeId,
      servers,
      toggleConnector,
    ],
  );

  return {
    connectorItems,
    attachedConnectorIds,
    handleAttachConnector,
  };
}

function useAllowConsulControl(): boolean {
  const [allowConsulControl, setAllowConsulControl] = useState(() =>
    readAllowConsulControl(),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setAllowConsulControl(readAllowConsulControl());
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return allowConsulControl;
}

function readAllowConsulControl(): boolean {
  if (typeof window === "undefined") return false;
  return buildSurfaceCapabilities(window.innerWidth).allowConsulControl;
}

function filterConnectorIds(
  ids: McpServerId[],
  options: { allowConsulControl: boolean },
): McpServerId[] {
  if (options.allowConsulControl) return ids;
  return ids.filter((id) => id !== "consul-control");
}
